import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Collapse, Button } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const FAQ = () => {
  const navigate = useNavigate();

  // State to manage which FAQ items are expanded
  const [expandedItems, setExpandedItems] = useState({
    0: true, // First item is expanded by default
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
  });

  const handleBackToHelp = () => {
    navigate("/help");
  };

  const toggleExpanded = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const faqData = [
    {
      question:
        "How do I download raw counts?",
      answer: `Raw counts can be downloaded from the data quality page. Navigate to that page and select the 
               'Counts table' option at the top of the page. The options section will allow you to choose 
               betweeen gene level or transcript level counts. You can also choose whether or not to aggregate
               counts from all selected files. By default the results will contain raw counts, if transript level
               counts are selected the count type can also be set to TPM or RPKM.`,
    },
    {
      question:
        "What are the codes that appear in the titles of EIRNABio Connect plots?",
      answer: `These are unique codes that are generated for every plot on EIRNABio Connect. These allow for quickly sharing plots and record the specific files/studies used to generate the plot as well as any settings that were changed.<br><br>
      By following a link in the form https://eirnabioconnect.com/short/short_code the plot can be recreated in the browser. For example if the code in the title of the plot was (l2I) following the link <a href="https://eirnabioconnect.com/short/l2I" target="_blank">https://eirnabioconnect.com/short/l2I</a> would load the plot in the browser.<br><br>
      Scrolling up the page would show you which settings and files were used to generate the plot. This link can be sent to others to facilitate easily sharing plots. Note, following a link to a short code will result in an error page if you are not logged in or you do not have permission to access that project.`,
    },
    {
      question: "How do I share plots?",
      answer: `For every plot generated on EIRNABio Connect a unique id is also generated and displayed in the plots title. A link can be generated using this unique id in the format https://eirnabioconnect.com/short/unique_code.<br><br>
      Sending this link to someone will recreate the plot on their browser using the same files/settings/organism used to create the plot. For convenience, a button will appear above the plot which will be labelled "Direct link to this plot". Right clicking on the button and clicking "copy link location" allows you to easily send the full link to others.`,
    },
    {
      question: "How is the Ribo-Seq/mRNA-Seq data processed?",
      answer: `For the majority of Ribo-seq projects unique molecular identifiers (UMIs) are used which allow PCR duplicates to be collapsed. We always communicate the details regarding the UMIs and sequence read architecture by email. Aside from UMIs, the typical data processing pipeline is the following: adapter sequences are removed using <a href="http://cutadapt.readthedocs.io/en/stable/guide.html" target="_blank">cutadapt</a>. Reads below 20 nucleotides in length after adapter removal are discarded.<br><br>
      Reads are aligned to rRNA and tRNA sequences using <a href="http://bowtie-bio.sourceforge.net/index.shtml" target="_blank">bowtie</a>. Reads that map to rRNA or tRNA are discarded while remaining reads are aligned to the transcriptome using <a href="http://bowtie-bio.sourceforge.net/index.shtml" target="_blank">bowtie</a>. UMI's are removed where applicable. The resulting BAM file is parsed with a python script. An offset is also determined for every read by choosing the highest peak upstream of the start codon using the counts from the metagene profile.`,
    },
    {
      question:
        "What is the difference between transcript level and gene level counts?",
      answer: `<b>Transcript level counts:</b> the number of reads that align to that particular transcript. Depending on the plot type these may be broken down into reads aligning to the 5' Leader (5' UTR), CDS, and 3' trailer (3'UTR) or a total for the entire transcript. Reads that align to more than one transcript from different genes or that align to multiple positions on the same transcript are classed as ambiguous.<br><br>
      <b>Gene level counts:</b> an aggregate of all counts for transcripts of that gene. A particular read that aligns to multiple transcripts will only be counted once. Reads are categorized in a hierarchical way giving priority to CDS regions, then 5' Leader (5'UTR) or 3' trailer (3' UTR) regions and finally non-coding regions.`,
    },
    {
      question: "How long does it usually take for a plot to complete?",
      answer: `It's difficult to give expected times for plots as it depends heavily on the options and files selected as well as the current computational load on the server. With default options, here are some rough estimates for how long each plot will take to complete:<br><br>
      Subcodon profiles, comparison profiles and most of the data quality plots typically complete very quickly (a few seconds at most), the main exception here is the counts table, if transcript level counts are requested this can take several minutes to return.`,
    },
    {
      question:
        "Why does the reading frame of the annotated CDS change on subcodon/comparison plots?",
      answer: `Which reading frame the annotated CDS ends up in is dictated by the length of the 5' Leader/5' UTR for the transcript in question. The first nucleotide of the transcript is always denoted as frame 1, the second nucleotide frame 2, the third frame 3 and then it repeats from there.<br><br>
      So if the 5' Leader length in nucleotides is divisible by 3 then the CDS will be in reading frame 1, but otherwise will end up in frame 2 or frame 3.`,
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-4">
          <Col lg={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Button
                  color="light"
                  size="sm"
                  onClick={handleBackToHelp}
                  style={{
                    marginRight: "16px",
                    border: "1px solid #d1d5db",
                    color: "#6b7280",
                  }}
                >
                  <i className="ri-arrow-left-line me-1"></i>
                  Back to Help
                </Button>
                <h4 className="mb-0 fw-bold" style={{ color: "#2F9E37" }}>
                  Frequently Asked Questions (FAQ)
                </h4>
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            {faqData.map((faq, index) => (
              <div
                key={index}
                style={{
                  borderLeft: "5px solid #2F9E37",
                  borderRight: "1px solid #2F9E37", // Thin border on right
                  borderTop: "1px solid #2F9E37", // Thin border on top
                  borderBottom: "1px solid #2F9E37", // Thin border on bottom
                  borderRadius: "4px",
                  marginBottom: "12px",
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                  overflow: "hidden",
                }}
              >
                {/* FAQ Question */}
                <div
                  style={{
                    padding: "14px 18px",
                    cursor: "pointer",
                    backgroundColor: expandedItems[index] ? "#e0f7ea" : "#fff",
                    transition: "background 0.2s",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  onClick={() => toggleExpanded(index)}
                  onMouseEnter={(e) => {
                    if (!expandedItems[index]) {
                      e.currentTarget.style.backgroundColor = "#f9fafb";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!expandedItems[index]) {
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                >
                  <span
                    style={{
                      color: "#1f2937",
                      fontSize: "15px",
                      fontWeight: 600,
                      flex: 1,
                      transition: "color 0.2s",
                    }}
                  >
                    {faq.question}
                  </span>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: "#f3f4f6",
                    }}
                  >
                    <i
                      className={
                        expandedItems[index]
                          ? "ri-subtract-line"
                          : "ri-add-line"
                      }
                      style={{
                        color: "#2F9E37",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    ></i>
                  </span>
                </div>
                <Collapse isOpen={expandedItems[index]}>
                  <div
                    style={{
                      background: "#fff",
                      padding: "0 18px 14px 18px",
                      fontSize: "14px",
                      color: "#374151",
                      lineHeight: "1.6",
                      marginTop: "12px",
                    }}
                  >
                    <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  </div>
                </Collapse>
              </div>
            ))}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default FAQ;
